import type { ReactNode, RefObject } from 'react'
import type { HeroCoordinates } from '@/components/heroes/hero-frame'
import type { LogEntry } from '@/lib/server/research'

import { useEffect, useRef } from 'react'
import { ArrowDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { onAdapted } from '@/lib/adaptation'
import { HeroFrame, useHero, useHeroProgress } from '@/components/heroes/hero-frame'
import { EclipseField } from '@/lib/heroes/eclipse-field'
import { loadConstellations, loadStars } from '@/lib/astro/catalogue'
import { registerSkyPointer } from '@/lib/sky-pointer'
import { prefersReducedMotion } from '@/lib/preferences'
import { formatKey, isDateKey } from '@/lib/time'
import styles from '@/components/heroes/research-hero.module.css'

import {
    ALGOL,
    ECLIPSE_HOURS,
    FRAME_SECONDS,
    MAG_BOTTOM,
    MAG_TOP,
    MEASUREMENTS,
    MINIMUM_AT,
    PERIOD_HOURS,
    algolMagnitude,
    formatUt,
    hourMarks
} from '@/lib/heroes/algol'

const TITLE_ID = 'research-hero-title'
const CLIP_ID = 'research-curve-clip'
const AREA_ID = 'research-curve-area'
/** The telescope is pointed at Algol. */
const TARGET: HeroCoordinates = { ra: ALGOL.ra, dec: ALGOL.dec }

/** Intro timeline, in seconds after the page has adapted: push in, lock on, open the apertures. */
const ZOOM_START = 0.3
const ZOOM_SECONDS = 4.7
const LOCK_START = 3
const LOCK_SECONDS = 1.9
const PHOTOMETRY_START = 4.6
const PHOTOMETRY_END = 5.5
const INTRO_END = 6
/** The eclipse then plays on its own over half a minute; scrolling runs it faster. */
const RUN_START = 5.4
const RUN_SECONDS = 30
const SCROLL_END = 0.8
/** Scroll progress over which the scene dissolves into the page. */
const DISSOLVE_START = 0.88
/** Field of view, in degrees, at the start of the push-in. */
const FOV_WIDE = 46

/** The light curve's plotting area inside its 400 by 150 viewBox. */
const PLOT = { left: 34, right: 392, top: 8, bottom: 126 }

const clamp01 = (value: number): number => (value < 0 ? 0 : value > 1 ? 1 : value)

const smoothstep = (edge0: number, edge1: number, value: number): number => {
    const t = clamp01((value - edge0) / (edge1 - edge0))

    return t * t * (3 - 2 * t)
}

const easeInOutCubic = (t: number): number => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2)

const easeInOutSine = (t: number): number => -(Math.cos(Math.PI * t) - 1) / 2

const xOf = (hours: number): number => PLOT.left + (hours / ECLIPSE_HOURS) * (PLOT.right - PLOT.left)

const yOf = (mag: number): number => PLOT.top + ((mag - MAG_TOP) / (MAG_BOTTOM - MAG_TOP)) * (PLOT.bottom - PLOT.top)

const CURVE = (() => {
    const points: string[] = []

    for (let step = 0; step <= 200; step++) {
        const hours = (step / 200) * ECLIPSE_HOURS

        points.push(`${xOf(hours).toFixed(1)},${yOf(algolMagnitude(hours)).toFixed(1)}`)
    }

    return {
        line: `M${points.join(' L')}`,
        area: `M${points.join(' L')} L${PLOT.right},${PLOT.bottom} L${PLOT.left},${PLOT.bottom} Z`
    }
})()

const MAG_TICKS = [2, 2.5, 3, 3.5]
const HOUR_MARKS = hourMarks()

/** Where Algol's companion sits in the little orbit diagram, from the hours into the window. */
const companionX = (hours: number): number => 60 + 46 * Math.sin((2 * Math.PI * (hours - MINIMUM_AT)) / PERIOD_HOURS)

const pad4 = (value: number): string => String(value).padStart(4, '0')

type Control = {
    skip: boolean
    wake: () => void
}

/** The light curve, plotted live: one measurement every six minutes, the fitted model drawn up to now. */
const LightCurve = ({ pointsRef, clipRef, cursorRef, headRef }: {
    pointsRef: RefObject<SVGGElement | null>
    clipRef: RefObject<SVGRectElement | null>
    cursorRef: RefObject<SVGLineElement | null>
    headRef: RefObject<SVGGElement | null>
}): ReactNode => (
    <svg viewBox='0 0 400 150' className='mt-3 block w-full overflow-visible' aria-hidden>
        <defs>
            <clipPath id={CLIP_ID}>
                <rect ref={clipRef} x={PLOT.left} y={0} width={0} height={150} />
            </clipPath>
            <linearGradient id={AREA_ID} x1='0' y1='0' x2='0' y2='1'>
                <stop offset='0' stopColor='var(--c-brass)' stopOpacity={0.2} />
                <stop offset='1' stopColor='var(--c-brass)' stopOpacity={0} />
            </linearGradient>
        </defs>

        {MAG_TICKS.map(mag => (
            <g key={mag}>
                <line x1={PLOT.left} y1={yOf(mag)} x2={PLOT.right} y2={yOf(mag)} stroke='var(--c-hair)' strokeWidth={0.6} strokeDasharray='1 4' />
                <text x={PLOT.left - 8} y={yOf(mag) + 3} textAnchor='end' fill='var(--c-star-3)' fontSize={8.5} className={styles.axis}>
                    {mag.toFixed(1)}
                </text>
            </g>
        ))}
        <line x1={PLOT.left} y1={PLOT.top} x2={PLOT.left} y2={PLOT.bottom} stroke='var(--c-hair-2)' strokeWidth={0.7} />
        <line x1={PLOT.left} y1={PLOT.bottom} x2={PLOT.right} y2={PLOT.bottom} stroke='var(--c-hair-2)' strokeWidth={0.7} />
        {HOUR_MARKS.map(mark => (
            <g key={mark.label}>
                <line x1={xOf(mark.hours)} y1={PLOT.bottom} x2={xOf(mark.hours)} y2={PLOT.bottom + 4} stroke='var(--c-hair-2)' strokeWidth={0.7} />
                <text x={xOf(mark.hours)} y={PLOT.bottom + 15} textAnchor='middle' fill='var(--c-star-3)' fontSize={8.5} className={styles.axis}>
                    {mark.label}
                </text>
            </g>
        ))}

        <g clipPath={`url(#${CLIP_ID})`}>
            <path d={CURVE.area} fill={`url(#${AREA_ID})`} />
            <path d={CURVE.line} fill='none' stroke='var(--c-brass)' strokeWidth={1.3} strokeLinejoin='round' />
        </g>

        <g className={styles.minimum}>
            <line x1={xOf(MINIMUM_AT)} y1={PLOT.top} x2={xOf(MINIMUM_AT)} y2={PLOT.bottom} stroke='var(--c-brass)' strokeWidth={0.7} strokeDasharray='2 3' />
            <text x={xOf(MINIMUM_AT) + 6} y={PLOT.bottom - 6} fill='var(--c-brass)' fontSize={8.5} className={styles.axis}>
                {`MIN ${formatUt(MINIMUM_AT)} UT`}
            </text>
        </g>

        <g ref={pointsRef}>
            {MEASUREMENTS.map(point => {
                const x = xOf(point.hours).toFixed(1)
                const y = yOf(point.mag)

                return (
                    <g key={point.hours} className={styles.point}>
                        <line x1={x} x2={x} y1={(y - point.error * 70).toFixed(1)} y2={(y + point.error * 70).toFixed(1)} stroke='var(--c-star-3)' strokeWidth={0.6} />
                        <circle cx={x} cy={y.toFixed(1)} r={1.7} fill='var(--c-star)' />
                    </g>
                )
            })}
        </g>

        <line ref={cursorRef} x1={PLOT.left} x2={PLOT.left} y1={PLOT.top} y2={PLOT.bottom} stroke='var(--c-brass)' strokeOpacity={0.35} strokeWidth={0.7} />
        <g ref={headRef} transform={`translate(${PLOT.left} ${yOf(ALGOL.mag).toFixed(1)})`}>
            <circle r={6} fill='none' stroke='var(--c-brass)' strokeWidth={0.7} className={styles.ping} />
            <circle r={2.6} fill='var(--c-brass)' />
        </g>
    </svg>
)

interface EclipseSceneProps {
    entry?: LogEntry | null
}

const EclipseScene = ({ entry }: EclipseSceneProps): ReactNode => {
    const { reduced, skipped, stickyRef, skip } = useHero()
    const wrapRef = useRef<HTMLDivElement | null>(null)
    const canvasRef = useRef<HTMLCanvasElement | null>(null)
    const panelRef = useRef<HTMLElement | null>(null)
    const clockRef = useRef<HTMLSpanElement | null>(null)
    const magRef = useRef<HTMLSpanElement | null>(null)
    const frameRef = useRef<HTMLSpanElement | null>(null)
    const statusRef = useRef<HTMLSpanElement | null>(null)
    const readoutRef = useRef<HTMLDivElement | null>(null)
    const insetRef = useRef<HTMLDivElement | null>(null)
    const companionRef = useRef<SVGCircleElement | null>(null)
    const pointsRef = useRef<SVGGElement | null>(null)
    const clipRef = useRef<SVGRectElement | null>(null)
    const cursorRef = useRef<SVGLineElement | null>(null)
    const headRef = useRef<SVGGElement | null>(null)
    const progressRef = useRef(0)
    const controlRef = useRef<Control>({ skip: false, wake: () => undefined })

    const latest = entry
        ? `${isDateKey(entry.observedOn) ? formatKey(entry.observedOn, 'short') : entry.observedOn} · ${entry.target}`
        : null

    useHeroProgress(progress => {
        progressRef.current = progress
        controlRef.current.wake()
    })

    useEffect(() => {
        if (!skipped) return

        controlRef.current.skip = true
        controlRef.current.wake()
    }, [skipped])

    useEffect(() => {
        const canvas = canvasRef.current
        const wrap = wrapRef.current
        const sticky = stickyRef.current
        const context = canvas ? canvas.getContext('2d', { alpha: false }) : null

        if (!canvas || !wrap || !sticky || !context) return

        const still = reduced || prefersReducedMotion()
        const phone = Math.min(window.innerWidth, window.innerHeight) < 640
        const fovEnd = phone ? 13 : 10
        const control = controlRef.current
        const field = new EclipseField(canvas, context, { phone })
        const points = pointsRef.current ? Array.from(pointsRef.current.children) : []
        const shownText = { clock: '', mag: '', frame: '' }
        const shownState = { photometry: -1, dissolve: -1, minimum: false, done: false }

        let alive = true
        let frame = 0
        let visible = true
        let last = 0
        let seconds = 0
        let waited = 0
        let adapted = false
        let intro = still ? INTRO_END : 0
        let hours = still ? ECLIPSE_HOURS : 0
        let shown = 0
        let pulseAt = -10
        let revealed = false

        const setText = (key: keyof typeof shownText, element: HTMLElement | null, value: string) => {
            if (shownText[key] === value) return

            shownText[key] = value
            if (element) element.textContent = value
        }

        const updateOverlays = (photometry: number, progress: number) => {
            const x = xOf(hours)

            clipRef.current?.setAttribute('width', (x - PLOT.left + 0.5).toFixed(1))
            cursorRef.current?.setAttribute('x1', x.toFixed(1))
            cursorRef.current?.setAttribute('x2', x.toFixed(1))
            headRef.current?.setAttribute('transform', `translate(${x.toFixed(1)} ${yOf(algolMagnitude(hours)).toFixed(1)})`)
            companionRef.current?.setAttribute('cx', companionX(hours).toFixed(2))

            setText('clock', clockRef.current, formatUt(hours))
            setText('mag', magRef.current, algolMagnitude(hours).toFixed(2))
            setText('frame', frameRef.current, `Frame ${pad4(Math.floor((hours * 3600) / FRAME_SECONDS) + 1)}`)

            const rounded = Math.round(photometry * 100) / 100

            if (rounded !== shownState.photometry) {
                shownState.photometry = rounded
                if (insetRef.current) insetRef.current.style.opacity = String(rounded)
                if (readoutRef.current) readoutRef.current.style.opacity = String(rounded)
            }

            const minimum = hours >= MINIMUM_AT + 0.4
            const done = hours >= ECLIPSE_HOURS - 1e-6

            if (minimum !== shownState.minimum) {
                shownState.minimum = minimum
                if (panelRef.current) panelRef.current.toggleAttribute('data-minimum', minimum)
            }

            if (done !== shownState.done) {
                shownState.done = done
                if (panelRef.current) panelRef.current.toggleAttribute('data-done', done)
                if (readoutRef.current) readoutRef.current.toggleAttribute('data-done', done)
                if (statusRef.current) statusRef.current.textContent = done ? 'Photometry · complete' : 'Photometry · live'
            }

            const dissolve = Math.round((still ? 0 : smoothstep(DISSOLVE_START, 1, progress)) * 200) / 200

            if (dissolve !== shownState.dissolve) {
                shownState.dissolve = dissolve
                wrap.style.opacity = dissolve > 0 ? String(1 - dissolve * 0.7) : ''
            }
        }

        const tick = (now: number) => {
            frame = 0

            if (!alive) return

            const dt = last ? Math.min(0.05, (now - last) / 1000) : 0

            last = now
            seconds += dt
            waited += dt

            const progress = still ? 0 : progressRef.current

            if (!still) {
                // Hold the push-in while the dark-adaptation intro covers the page.
                if (!adapted && waited > 6) adapted = true

                if (control.skip) {
                    intro = Math.max(intro, INTRO_END)
                    hours = ECLIPSE_HOURS
                }
                else if (adapted) {
                    // Scrolling during the intro hurries it along, so the scroll always drives the eclipse.
                    intro += dt * (progress > 0.01 && intro < INTRO_END ? 4 : 1)

                    const auto = ECLIPSE_HOURS * clamp01((intro - RUN_START) / RUN_SECONDS)
                    const scrolled = intro >= RUN_START ? ECLIPSE_HOURS * easeInOutSine(clamp01(progress / SCROLL_END)) : 0
                    const target = Math.max(auto, scrolled)

                    // Measurements are never taken back: scrolling up leaves the curve as far as it got.
                    if (target > hours) hours = Math.min(ECLIPSE_HOURS, target)
                }
            }

            const fov = FOV_WIDE + (fovEnd - FOV_WIDE) * easeInOutCubic(clamp01((intro - ZOOM_START) / ZOOM_SECONDS))
            const lock = clamp01((intro - LOCK_START) / LOCK_SECONDS)
            const photometry = smoothstep(PHOTOMETRY_START, PHOTOMETRY_END, intro)

            let count = shown

            while (count < MEASUREMENTS.length && MEASUREMENTS[count].hours <= hours + 1e-6) count++

            if (count > shown) {
                for (let i = shown; i < count; i++) points[i]?.setAttribute('data-on', '')

                shown = count
                if (!still) pulseAt = seconds
            }

            const pulse = still ? 0 : Math.max(0, 1 - (seconds - pulseAt) / 0.45)

            field.render({ seconds, fov, lock, photometry, magnitude: algolMagnitude(hours), pulse })

            if (!revealed) {
                revealed = true
                canvas.style.opacity = '1'
            }

            updateOverlays(photometry, progress)

            // The sky keeps scintillating, so the scene runs while it is on screen; a still frame is drawn once.
            if (!still && visible && !document.hidden) frame = requestAnimationFrame(tick)
        }

        const wake = () => {
            if (!alive || frame || !visible || document.hidden) return

            last = 0
            frame = requestAnimationFrame(tick)
        }

        const stop = () => {
            if (frame) cancelAnimationFrame(frame)
            frame = 0
        }

        control.wake = wake
        canvas.style.transition = still ? 'none' : ''

        const resize = () => {
            const width = wrap.clientWidth
            const height = wrap.clientHeight

            if (!width || !height) return

            field.resize(width, height, Math.min(window.devicePixelRatio || 1, phone ? 1.5 : 2))
            wake()
        }

        const resizeObserver = new ResizeObserver(resize)

        resizeObserver.observe(wrap)
        resize()

        const intersection = new IntersectionObserver(records => {
            // Several records can arrive at once: the last one is the current state.
            const record = records[records.length - 1]

            if (!record) return

            visible = record.isIntersecting

            if (visible) wake()
            else stop()
        })

        intersection.observe(sticky)

        const onVisibility = () => {
            if (document.hidden) stop()
            else wake()
        }

        document.addEventListener('visibilitychange', onVisibility)

        const stopAdapted = onAdapted(() => {
            adapted = true
            wake()
        })

        const unregister = registerSkyPointer(sticky, (clientX, clientY) => {
            const rect = canvas.getBoundingClientRect()

            return field.pick(clientX - rect.left, clientY - rect.top)
        })

        loadStars().then(catalogue => {
            if (!alive) return

            field.setCatalogue(catalogue)
            wake()
        }).catch(() => undefined)

        loadConstellations().then(figures => {
            if (!alive) return

            field.setFigures(figures)
            wake()
        }).catch(() => undefined)

        wake()

        return () => {
            alive = false
            control.wake = () => undefined
            stop()
            resizeObserver.disconnect()
            intersection.disconnect()
            document.removeEventListener('visibilitychange', onVisibility)
            stopAdapted()
            unregister()
        }
    }, [reduced, stickyRef])

    return (
        <>
            <div ref={wrapRef} aria-hidden className='media absolute inset-0'>
                <canvas ref={canvasRef} className='block h-full w-full opacity-0 transition-opacity duration-[1400ms] ease-out' />
            </div>

            <p className='sr-only'>
                The view through the observatory&apos;s 40 cm reflector: the camera pushes in from the constellation Perseus to the star Algol, a brass reticle locks on, and a light curve plots Algol&apos;s brightness frame by frame as it fades by two thirds and recovers over a ten hour eclipse.
            </p>

            <span aria-hidden className='pointer-events-none absolute inset-x-0 bottom-0 z-[5] h-[60%] bg-gradient-to-t from-ink via-ink/70 to-transparent md:h-[46%]' />

            {/* A model of the pair, beside the reticle: the dim companion crossing the bright star. */}
            <div
                ref={insetRef}
                aria-hidden
                style={{ opacity: 0 }}
                className='pointer-events-none absolute left-[calc(50%_+_min(12vw,12vh)_*_1.35)] top-[calc(40%_-_2.6rem)] z-10 hidden [@media(min-width:768px)_and_(orientation:landscape)]:block'
            >
                <span className='absolute right-full top-[2.55rem] mr-2 block h-px w-6 bg-brass/40' />
                <svg viewBox='0 0 120 70' className='w-36'>
                    <ellipse cx={60} cy={35} rx={46} ry={5} fill='none' stroke='var(--c-hair-2)' strokeWidth={0.6} strokeDasharray='1.5 2.5' />
                    <circle cx={60} cy={34} r={9} fill='var(--c-star)' className={styles.primary} />
                    <circle ref={companionRef} cx={companionX(0)} cy={36} r={11} fill='var(--c-ink-3)' stroke='var(--c-brass)' strokeOpacity={0.6} strokeWidth={0.6} />
                </svg>
                <p className='label-mono mt-1 text-[0.6rem] leading-relaxed text-star-3'>
                    <span className='text-brass'>A</span>
                    {' B8 V  ·  '}
                    <span className='text-brass'>B</span>
                    {' K0 IV'}
                    <br />
                    {`P ${(PERIOD_HOURS / 24).toFixed(3)} d`}
                </p>
            </div>

            <div
                ref={readoutRef}
                style={{ opacity: 0 }}
                className={cn('pointer-events-none absolute right-5 top-[5.25rem] z-10 hidden text-right sm:right-8 sm:block lg:right-12', styles.readout)}
            >
                <p className='label-mono flex items-center justify-end gap-2 text-star-3'>
                    <span aria-hidden className={cn('block size-1.5 rounded-full bg-signal', styles.live)} />
                    <span ref={statusRef}>
                        Photometry · live
                    </span>
                </p>
                <p className='data-mono mt-2 text-star'>
                    <span ref={frameRef}>
                        Frame 0001
                    </span>
                    <span className='text-star-3'>
                        {` · ${FRAME_SECONDS} s · V`}
                    </span>
                </p>
                <p aria-hidden className='label-mono mt-1.5 text-brass'>
                    40 cm reflector · CCD −20 °C
                </p>
            </div>

            <div className='pointer-events-none absolute inset-x-0 bottom-0 z-10 pb-[4.5rem] sm:pb-16'>
                <div className='container-page flex flex-col-reverse gap-6 md:flex-row md:items-end md:justify-between md:gap-10 [@media(max-height:560px)]:flex-row [@media(max-height:560px)]:items-end'>
                    <div className='min-w-0 max-w-[34rem] md:flex-1'>
                        <p className={cn('label-mono text-brass [@media(max-height:560px)]:hidden', styles.rise)}>
                            Research programme · Eclipse timing
                        </p>
                        <h1
                            id={TITLE_ID}
                            className={cn('display-serif mt-3 text-[2.75rem] text-star sm:text-6xl lg:text-7xl [@media(max-height:560px)]:text-4xl', styles.rise, styles.second)}
                        >
                            Patient light
                        </h1>
                        <p className={cn('mt-4 max-w-md text-base text-star-2 sm:text-lg [@media(max-height:700px)]:hidden', styles.rise, styles.third)}>
                            Every 2.87 days Algol loses two thirds of its light for ten hours. We time each eclipse to the second, year after year.
                        </p>
                        {latest && (
                            <a
                                href='#logbook'
                                onClick={event => {
                                    event.preventDefault()
                                    skip()
                                }}
                                className={cn('label-mono group pointer-events-auto mt-5 inline-flex items-center gap-2 py-1 text-star-3 transition-colors duration-500 hover:text-star [@media(max-height:560px)]:hidden', styles.rise, styles.fourth)}
                            >
                                <span className='h-px w-6 bg-brass/60 transition-all duration-500 group-hover:w-9 group-hover:bg-brass' />
                                {`Latest in the log · ${latest}`}
                                <ArrowDown aria-hidden className='size-4' />
                            </a>
                        )}
                    </div>

                    <figure
                        ref={panelRef}
                        className={cn('w-full max-w-[30rem] border border-hair bg-ink/70 p-4 sm:p-5 md:w-[min(31rem,46%)] md:max-w-none [@media(max-height:560px)]:w-[min(22rem,44%)] [@media(max-height:560px)]:p-3', styles.panel)}
                    >
                        <div className='flex items-baseline justify-between gap-4 border-b border-hair pb-2'>
                            <p className='label-mono text-brass'>
                                Algol · Primary eclipse
                            </p>
                            <p className='data-mono whitespace-nowrap text-[0.75rem] text-star-2'>
                                <span className='text-star-3'>UT </span>
                                <span ref={clockRef}>
                                    {formatUt(0)}
                                </span>
                                <span className='text-star-3'> · V </span>
                                <span ref={magRef}>
                                    {ALGOL.mag.toFixed(2)}
                                </span>
                            </p>
                        </div>
                        <LightCurve pointsRef={pointsRef} clipRef={clipRef} cursorRef={cursorRef} headRef={headRef} />
                        <figcaption className='label-mono mt-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-[0.6rem] text-star-3'>
                            <span>
                                V magnitude against UT
                            </span>
                            <span className={cn('text-brass', styles.result)}>
                                {`Minimum ${formatUt(MINIMUM_AT)} UT · O−C +4.1 min`}
                            </span>
                            <span className='sr-only'>
                                {`A light curve of Algol: its brightness falls from magnitude ${ALGOL.mag.toFixed(2)} to about 3.4 and recovers over ten hours, with the minimum timed at ${formatUt(MINIMUM_AT)} UT.`}
                            </span>
                        </figcaption>
                    </figure>
                </div>
            </div>
        </>
    )
}

interface ResearchHeroProps {
    /** The latest entry in the log, linked from the hero. */
    entry?: LogEntry | null
    /** The id of the logbook list right after the hero; "Skip intro" lands there. */
    contentId: string
}

/**
 * Research hero: the view through the 40 cm reflector during one eclipse of Algol. The camera pushes in from
 * Perseus, a brass reticle locks on, and the light curve is measured frame by frame as the star fades and
 * recovers, with scrolling running the night forward.
 */
export const ResearchHero = ({ entry, contentId }: ResearchHeroProps): ReactNode => (
    <HeroFrame
        id='research-hero'
        label='Research'
        coordinates={TARGET}
        screens={1.5}
        contentId={contentId}
        titleId={TITLE_ID}
        sky
    >
        <EclipseScene entry={entry} />
    </HeroFrame>
)
